import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, IconButton, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowBackIosNewRounded } from "@mui/icons-material";

export default function GoBack({
  url = null,
  title,
  variant = "text",
  color = "primary",
  size = "medium",
  ignoreParams = false,
  testId,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const samePageEntries = useSamePageHistoryEntries(location);

  const handleGoBack = () => {
    if (url) {
      const savedFilters = location.state?.filters;
      if (savedFilters && Object.keys(savedFilters).length > 0) {
        const params = new URLSearchParams(savedFilters);
        navigate(`${url}?${params.toString()}`);
        return;
      } else {
        navigate(url);
        return;
      }
    }
    if (location.key !== "default") {
      navigate(ignoreParams ? -(samePageEntries() + 1) : -1);
      return;
    } else {
      navigate(url || "/"); // Fallback to home if no URL provided
      return;
    }
  };

  // Modern icon-only button with better styling
  if (!title) {
    return (
      <IconButton
        title={t("GoBack")}
        onClick={handleGoBack}
        aria-label={t("GoBack")}
        data-testid={testId}
        sx={{
          borderRadius: 2,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateX(-2px)",
            backgroundColor: "action.hover",
          },
        }}
        size={size}
      >
        <ArrowBackIosNewRounded
          fontSize={size === "small" ? "small" : "medium"}
        />
      </IconButton>
    );
  }

  // Modern text button with icon
  return (
    <Box sx={{ display: "inline-flex" }}>
      <Button
        variant={variant}
        color={color}
        onClick={handleGoBack}
        startIcon={<ArrowBackIosNewRounded />}
        data-testid={testId}
        sx={{
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 2,
          px: 2,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.15)",
          },
          transition: "all 0.2s ease-in-out",
        }}
        size={size}
      >
        {t(title)}
      </Button>
    </Box>
  );
}

// Uses the router's history index, since one filter change can push several entries in a single render
function useSamePageHistoryEntries(location) {
  const firstIdx = React.useRef(getHistoryIdx());
  const lastPathname = React.useRef(location.pathname);

  React.useEffect(() => {
    const idx = getHistoryIdx();
    if (
      location.pathname !== lastPathname.current ||
      idx == null ||
      firstIdx.current == null ||
      idx < firstIdx.current
    ) {
      firstIdx.current = idx;
    }
    lastPathname.current = location.pathname;
  }, [location.key, location.pathname]);

  return React.useCallback(() => {
    const idx = getHistoryIdx();
    if (idx == null || firstIdx.current == null) return 0;
    return Math.max(0, idx - firstIdx.current);
  }, []);
}

function getHistoryIdx() {
  const idx = window.history.state?.idx;
  return typeof idx === "number" ? idx : null;
}
